import React, { useCallback, useRef, useState } from 'react';
import moment from 'moment';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import { Modal, Row } from 'react-bootstrap';
import styles from './calendar.scss';
import Tile from '../../shared/tile/tile';
import { LocaleWrapper, WIDGET_LOCALE } from '../../../../locale-wrapper';

export const ModalEvent = ({ calendarRef, event, settings, setOpen, open, onResizeComponent }) => {
  const maxHeight = calendarRef.current.clientHeight;
  const bg = {
    maxHeight,
    overlay: {
      background: '#FFFFFF',
    },
  };
  return (
    <Modal show={open} onHide={() => setOpen(false)} styles={bg} dialogClassName={styles.dialogClassName} height={maxHeight}>
      <Modal.Header closeButton>
        <Modal.Title>{event.get('title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <LocaleWrapper event={event}>
          <Tile event={event} settings={settings} withIcon onResizeComponent={onResizeComponent} />
        </LocaleWrapper>
      </Modal.Body>
    </Modal>
  );
};

const DayHeaderComponent = ({ dayText, dayNumber }) => (
  <div className={styles.dayHeaderWrapper}>
    <div className={styles.dayText}>{dayText}</div>
    <div className={styles.dayNumber}>{dayNumber}</div>
  </div>
);

const CalendarContainer = ({ events, settings, onResizeComponent }) => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [open, setOpen] = useState(false);

  const calendarRef = useRef();
  // Convert events to the format required by FullCalendar
  const calendarEvents = events
    .map((event) => {
      const allDay = event.get('allDay');
      const startTime = event.getIn(['date', 'start', 'utc']);
      const timezone = event.getIn(['date', 'start', 'timezone']);
      let start = moment(startTime).utc().format();
      if (timezone) {
        start = moment(startTime).add({ hour: 1 }).tz(timezone).format('YYYY-MM-DDTHH:mm:ssZ');
      }
      const endTime = event.getIn(['date', 'end', 'utc']);
      const isAfter = moment(endTime).isAfter(moment(startTime), 'day');
      let end = moment(endTime)
        .add(isAfter ? 1 : 0, 'day')
        .utc()
        .format();
      if (timezone) {
        end = moment(endTime).tz(timezone).format('YYYY-MM-DDTHH:mm:ssZ');
      }
      return {
        id: event.get('id'),
        title: event.get('title'),
        allDay,
        start,
        end,
      };
    })
    .toJS();

  const dayHeaderContent = useCallback((date) => {
    const formatDate = moment(date);
    const dayText = formatDate.format('ddd');
    const dayNumber = formatDate.get('day');
    return <DayHeaderComponent dayText={dayText} dayNumber={dayNumber} />;
  }, []);

  const configToViews = {
    month: 'dayGridMonth',
    week: 'timeGridWeek',
    day: 'timeGridDay',
    list: 'listWeek',
  };
  return (
    <div id="calendar-layout" className={styles.container}>
      <Row ref={calendarRef}>
        <FullCalendar
          titleFormat={{
            month: 'long',
            year: 'numeric',
          }}
          dayHeaderFormat={{
            weekday: 'short',
            day: 'numeric',
          }}
          dayHeaderContent={(params) => dayHeaderContent(params.date)}
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
          slotMinTime={`${settings.get('calendarStartTime') - 1}:00:00`}
          slotMaxTime={`${settings.get('calendarEndTime') + 1}:00:00`}
          locale={WIDGET_LOCALE}
          displayEventTime={settings.get('showCalendarTime')}
          firstDay={settings.get('calendarFirstDay')}
          nextDayThreshold={`${settings.get('calendarNextDayThreshold')}:00:00`}
          dayMaxEvents
          selectable
          weekends={settings.get('showCalendarWeekends')}
          height="auto"
          eventClick={(arg) => {
            const { id } = arg.event;
            const event = events.find((e) => e.get('id').toString() === id.toString());
            setSelectedEvent(event);
            setOpen(true);
          }}
          eventTimeFormat={{
            hour: 'numeric',
            minute: '2-digit',
            meridiem: 'short',
          }}
          initialView={configToViews[settings.get('calendarView')]}
          headerToolbar={{
            left: 'prev,next',
            center: 'title',
            right: `${settings.get('showCalendarToday') ? 'today' : ''}`,
          }}
          slotLabelContent={(label) => {
            if (new Date(label.date).getHours() === settings.get('calendarStartTime') - 1) return <></>;
            return <>{moment(label.date).format('h A')}</>;
          }}
          events={calendarEvents}
        />
        {selectedEvent && (
          <ModalEvent
            calendarRef={calendarRef}
            event={selectedEvent}
            settings={settings}
            setOpen={setOpen}
            open={open}
            onResizeComponent={onResizeComponent}
          />
        )}
      </Row>
    </div>
  );
};

export default CalendarContainer;
